// @givmo/board — a written consent's compensation details (CBG-YR4-PAY-CONSENT; the backend's
// `board-compensation-decisions.md` §7, §8.2; no frame drew it).
//
// ══ WHAT THE PANEL HOLDS ════════════════════════════════════════════════════════════════════════
//
// While the consent is a DRAFT, the officer records one decision per person whose pay it sets, and
// uploads onto each the documents the board relied on; Board reads each file's bytes and takes its
// fingerprint at the confirm. All the decisions print as ONE block, which Board serves and the
// officer's act places in the consent's text ("Add to the consent's text"): the consent cannot go
// out, or be revised, unless its text carries the current block exactly, and the directors sign the
// text, so they sign the block and, through each fingerprint, the documents.
//
// From the first circulation everything here stands as it was sent (C10): no change, no upload, no
// removal. To change the details, the officer withdraws the consent and drafts another. The
// organization's Form 990 answer is a label for its return, outside the text (C3, C4): it is shown
// and changed on Form 990 › Compensation decisions, not here.
//
// ⚠ An upload is three steps — the signed URL, the bytes straight to storage, the confirm — exactly
// the packet's (`packets/attachments-panel.tsx`), and the same two headers ride the PUT because both
// are signed. A file over Board's cap is stopped before any byte leaves, in the backend's own words.

import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import {
  isGivmoError,
  type BoardCompensationDecisionOut,
  type BoardCompensationDocumentOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  ErrorMessage,
  Icon,
  IconButton,
  RowActions,
  toast,
} from '@givmo/design-system';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { useBoardAnswersForm990, useBoardOperations } from '../../board-runtime';
import { StatusWord } from '../../components/fine-print';
import { ATTACHMENT_ACCEPT, displaySize, isAllowedAttachment } from '../packets/packets-decode';
import { carriesBlock, holdsBlock, placeBlock } from './compensation-block';
import { ConsentDecisionDialog } from './compensation-dialogs';
import { useConsentCompensation, useRefreshCompensation } from './compensation-queries';
import { compensationWords as w } from './compensation-words';

/** Board's read cap: 100 MiB (the contract's §3, `COMPENSATION_DOCUMENT_MAX_BYTES`). */
export const DOCUMENT_MAX_BYTES = 100 * 1024 * 1024;

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s12 font-w400 tracking-givmo text-text-tertiary';
const DOC_LINK =
  'min-w-0 text-left font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors [overflow-wrap:anywhere] hover:text-brand-700 focus-visible:underline';

function failure(caught: unknown, fallback: string): string {
  if (isGivmoError(caught)) return caught.message;
  return caught instanceof Error && caught.message ? caught.message : fallback;
}

/** One decision's documents: each opens its fingerprinted bytes; while a draft, one more can go on. */
function DecisionDocuments({
  orgRef,
  consentId,
  decision,
  editable,
  onChanged,
}: {
  orgRef: string;
  consentId: string;
  decision: BoardCompensationDecisionOut;
  editable: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const operations = useBoardOperations();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const documents = decision.relied_on?.documents ?? [];
  const path = { org_ref: orgRef, consent_id: consentId, decision_id: decision.id };

  const upload = useMutation({
    mutationFn: async (file: File) => {
      // 1. A place for it: the key is minted server-side, under this decision's own prefix.
      const issued = await operations.officerIssueBoardCompensationDocumentUploadUrl({
        path,
        body: { content_type: file.type, file_name: file.name },
        throwOnError: true,
      });
      // 2. The bytes straight to storage, not through the api-client. Both headers are signed.
      const stored = await fetch(issued.data.upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': issued.data.content_type,
          'Content-Disposition': 'attachment',
        },
        body: file,
      });
      if (!stored.ok) throw new Error(w.saveFailed);
      // 3. The confirm: Board reads the stored bytes and takes their fingerprint.
      const { data } = await operations.officerConfirmBoardCompensationDocument({
        path,
        body: {
          object_ref: issued.data.object_ref,
          file_name: file.name,
          content_type: issued.data.content_type,
        },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: async (document) => {
      setError(null);
      await onChanged();
      toast.success(w.documentAdded(document.file_name));
    },
    onError: (caught) => setError(failure(caught, w.saveFailed)),
    onSettled: () => setUploading(null),
  });

  const remove = useMutation({
    mutationFn: async (document: BoardCompensationDocumentOut) => {
      await operations.officerRemoveBoardCompensationDocument({
        path: { ...path, document_id: document.id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await onChanged();
      toast.success(w.documentRemoved);
    },
    onError: (caught) => toast.error(failure(caught, w.removeFailed)),
  });

  const open = useMutation({
    mutationFn: async (document: BoardCompensationDocumentOut) => {
      const { data } = await operations.officerGetBoardCompensationDocumentDownload({
        path: { ...path, document_id: document.id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (link) => void window.open(link.download_url, '_blank', 'noopener,noreferrer'),
    onError: (caught) => toast.error(failure(caught, w.openFailed)),
  });

  function onPicked(file: File | null) {
    if (file === null) return;
    setError(null);
    if (!isAllowedAttachment(file.type)) {
      setError(w.wrongType);
      return;
    }
    if (file.size > DOCUMENT_MAX_BYTES) {
      setError(w.tooLarge);
      return;
    }
    setUploading(file.name);
    upload.mutate(file);
  }

  return (
    <div className="flex flex-col gap-6 border-l-2 border-border-strong pl-12">
      {documents.length > 0 || uploading !== null ? (
        <ul
          className="flex flex-col gap-4"
          data-testid={`consent-compensation-documents-${decision.id}`}
        >
          {documents.map((document) => (
            <li key={document.id} className="flex items-center justify-between gap-8">
              <span className="flex min-w-0 flex-wrap items-baseline gap-x-8">
                <button
                  type="button"
                  className={DOC_LINK}
                  disabled={open.isPending && open.variables?.id === document.id}
                  onClick={() => open.mutate(document)}
                  data-testid={`consent-compensation-document-${document.id}`}
                >
                  {document.file_name}
                </button>
                {displaySize(document.byte_size) !== null ? (
                  <span className={QUIET}>{displaySize(document.byte_size)}</span>
                ) : null}
              </span>
              {editable ? (
                <IconButton
                  aria-label={`${w.remove} ${document.file_name}`}
                  tone="ghost"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(document)}
                  testId={`consent-compensation-document-remove-${document.id}`}
                >
                  <Icon icon={X} size={16} aria-hidden />
                </IconButton>
              ) : null}
            </li>
          ))}
          {uploading !== null ? (
            <li className="flex items-baseline gap-8" data-testid="consent-compensation-uploading">
              <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                {uploading}
              </span>
              <StatusWord tone="quiet">{w.uploading}</StatusWord>
            </li>
          ) : null}
        </ul>
      ) : null}
      {error !== null ? (
        <ErrorMessage testId={`consent-compensation-upload-error-${decision.id}`}>
          {error}
        </ErrorMessage>
      ) : null}
      {/* While a file is on its way the row above says so; the act comes back once it has landed. */}
      {editable && !upload.isPending ? (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={ATTACHMENT_ACCEPT}
            className="sr-only"
            onChange={(event) => {
              onPicked(event.target.files?.[0] ?? null);
              event.target.value = '';
            }}
            data-testid={`consent-compensation-file-${decision.id}`}
          />
          <Button
            appearance="saas"
            tone="ghost"
            size="sm"
            className="self-start"
            onClick={() => inputRef.current?.click()}
            testId={`consent-compensation-add-document-${decision.id}`}
          >
            {w.addDocument}
          </Button>
        </>
      ) : null}
    </div>
  );
}

export function ConsentCompensationPanel({
  consentId,
  committee = false,
  body,
  textBeingEdited,
}: {
  consentId: string;
  /** CBG-YR6: a committee's written consent, whose members sign its block. */
  committee?: boolean;
  /** The live version's text, where the block is placed. */
  body: string;
  /** The officer's own editor is open: the text isn't placed into meanwhile. */
  textBeingEdited: boolean;
}) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const { orgRef, isReady } = useBoardHost();
  const answersForm990 = useBoardAnswersForm990();
  const canWrite = useBoardPermission('board_record:write');
  const compensationQuery = useConsentCompensation(orgRef, consentId, isReady);
  const refreshCompensation = useRefreshCompensation(orgRef);
  const [editing, setEditing] = useState<BoardCompensationDecisionOut | null | 'new'>(null);
  const org = orgRef ?? '';
  const consentKey = ['board-written-consent', orgRef, consentId] as const;

  const refreshAll = () =>
    Promise.all([refreshCompensation(), queryClient.invalidateQueries({ queryKey: consentKey })]);

  const removeDecision = useMutation({
    mutationFn: async (decision: BoardCompensationDecisionOut) => {
      await operations.officerRemoveBoardConsentCompensationDecision({
        path: { org_ref: org, consent_id: consentId, decision_id: decision.id },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await refreshAll();
      toast.success(w.removed);
    },
    onError: (caught) => toast.error(failure(caught, w.removeFailed)),
  });

  const compensation = compensationQuery.data ?? null;

  const placeInText = useMutation({
    mutationFn: async () => {
      const block = compensation?.textBlock ?? '';
      await operations.officerUpdateBoardWrittenConsent({
        path: { org_ref: org, consent_id: consentId },
        body: { body: placeBlock(body, block) },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      await refreshAll();
      toast.success(w.blockAdded);
    },
    onError: (caught) => toast.error(failure(caught, w.saveFailed)),
  });

  // Not served (its route miss), still reading, or a failed read: the page draws none of it — the
  // consent's own text and acts stand without it, and a refused circulation still says why.
  if (compensation === null || orgRef === null) return null;
  // The text carries the details only when it holds the CURRENT block exactly (`carriesBlock`), the
  // same test the circulation and the version's CHECK make (CBG-FIX84). With no details left, a block
  // still in a draft's text is said too: the backend refuses to circulate or revise it then.
  const { decisions, editable } = compensation;
  if (decisions.length === 0 && !(editable && canWrite)) return null;
  const blockLeft = decisions.length === 0 && editable && holdsBlock(body);

  return (
    <Card testId="consent-compensation">
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-wrap items-start justify-between gap-12">
          <div className="flex flex-col gap-4">
            <h2 className="font-sans text-s15 font-w600 tracking-givmo text-text-strong">
              {w.title}
            </h2>
            {decisions.length === 0 ? (
              <p className={SECONDARY}>{committee ? w.committeeConsentLead : w.consentLead}</p>
            ) : null}
          </div>
          {editable && canWrite ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => setEditing('new')}
              testId="consent-compensation-add"
            >
              {w.addDetails}
            </Button>
          ) : null}
        </div>

        {decisions.length > 0 ? (
          <ul className="flex flex-col gap-12">
            {decisions.map((decision) => {
              const actions =
                editable && canWrite
                  ? [
                      {
                        label: w.edit,
                        testId: `consent-compensation-edit-${decision.id}`,
                        onSelect: () => setEditing(decision),
                      },
                      {
                        label: w.remove,
                        testId: `consent-compensation-remove-${decision.id}`,
                        destructive: true,
                        onSelect: () => removeDecision.mutate(decision),
                      },
                    ]
                  : [];
              return (
                <li
                  key={decision.id}
                  className="flex flex-col gap-8"
                  data-testid={`consent-compensation-row-${decision.id}`}
                >
                  <div className="flex items-start justify-between gap-8">
                    {/* Whose pay; the documents it names are listed right beneath. */}
                    <span className="min-w-0 font-sans text-s14 font-w600 tracking-givmo text-text-strong">
                      {decision.position
                        ? `${decision.person_name}, ${decision.position}`
                        : decision.person_name}
                    </span>
                    {actions.length > 0 ? (
                      <RowActions
                        label={w.rowActions(decision.person_name)}
                        testId={`consent-compensation-actions-${decision.id}`}
                        actions={actions}
                      />
                    ) : null}
                  </div>
                  <DecisionDocuments
                    orgRef={orgRef}
                    consentId={consentId}
                    decision={decision}
                    editable={editable && canWrite}
                    onChanged={refreshAll}
                  />
                </li>
              );
            })}
          </ul>
        ) : null}

        {blockLeft ? (
          <p
            className="font-sans text-s13 font-w600 leading-h15 tracking-givmo text-warning-700"
            data-testid="consent-compensation-block-left"
          >
            {w.blockLeft}
          </p>
        ) : null}

        {decisions.length > 0 ? (
          !editable ? (
            <p className={QUIET} data-testid="consent-compensation-gone-out">
              {w.goneOut}
            </p>
          ) : compensation.textCarriesBlock && carriesBlock(body, compensation.textBlock) ? (
            <p className={QUIET} data-testid="consent-compensation-in-text">
              {committee ? w.committeeBlockInText : w.blockInText}
            </p>
          ) : (
            <div
              className="flex flex-wrap items-center justify-between gap-10"
              data-testid="consent-compensation-block-missing"
            >
              {/* The act tone (the one colour a state someone must act on takes), wrapping: the line
                  is longer than a status word. */}
              <span className="min-w-0 font-sans text-s13 font-w600 leading-h15 tracking-givmo text-warning-700">
                {w.blockMissing}
              </span>
              {canWrite && compensation.textBlock !== null ? (
                // Secondary: the page's one primary act stays "Put it to the board".
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  loading={placeInText.isPending}
                  disabled={textBeingEdited}
                  onClick={() => placeInText.mutate()}
                  testId="consent-compensation-add-to-text"
                >
                  {w.addToText}
                </Button>
              ) : null}
            </div>
          )
        ) : null}
      </CardBody>

      {editing !== null ? (
        <ConsentDecisionDialog
          orgRef={orgRef}
          consentId={consentId}
          committee={committee}
          decision={editing === 'new' ? null : editing}
          answersForm990={answersForm990}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refreshAll();
          }}
        />
      ) : null}
    </Card>
  );
}
